import {
  Action,
  ActionReducer,
  createFeatureSelector,
  createReducer,
  createSelector,
  MemoizedSelector,
  on,
} from '@ngrx/store';

import * as ShopCartActions from '../actions/shop-cart.actions';
import { shopName } from '../utils/localStorage.utils';
import { ICart, ICartBaseInformation, ICartItem, ICartItemListError, ICartRule, ICartSwitchDecision, ITotalWeight } from '../models/cart.models';
import { PayloadUtils } from '../utils/payload.utils';
import { ISapMessage } from '../models/sap.model';
import { EGlueResource } from '../configurations/common';
import {
  IBaseModel,
  IMaterialMasterNumbersPerCart,
  IMaterialMasterNumbersPerItem,
  IPriceDisputingPerItem,
} from '../models/common.models';
import { CartUtils } from '../utils/cart.utils';

export interface ShopCartState {
  carts: Array<ICart>;
  currentCartId: string | null;
  cartItems: any;
  cart: any;
  currentDefaultCartEtag: string | null;
  updateItemInCartData: {
    'data': {
      'type': string,
      'attributes': {
        'quantity': number | null;
      }
    }
  };
  isCartEmpty: boolean;  //TODO: do not store calculable values, create selector with logic related to this
  cartItemsWithDetails: Array<any>; //TODO: also stored in cartItems.included[i] ('items'), has to be check if data are equal
  rfqActive: boolean; //TODO: can be countable in selector
  cartRules: Array<ICartRule>; //TODO: stored in cart included
  sapMessages: Array<ISapMessage>; //TODO: also stored in cartItems.included[i] (sap-messages).attributes.messages[i], can be identified by materialNumber
  included: Array<any>; // Contains includes (e.g. cart items, product image sets for OTHER carts) -> needs to be kept
  priceDisputingPerItem: IPriceDisputingPerItem[];
  materialMasterNumbersPerCart: IMaterialMasterNumbersPerCart[];
  //Operations
  areAllDataLoadedForCheckout: boolean;
  itemsInQueueToCart: Array<string>;
  lastAddedItemName: string;
  cartOperationInProgress: boolean;
  isMiniCartOpen: boolean;
  showNewItemAddedNotification: boolean;
  pendingCartSwitchDecision: ICartSwitchDecision | null;
  addItemToCartOperationInProgress: boolean;
  loadingCartDataInProgress: boolean;
  addingToCartViaCsvInProgress: boolean;
  //Errors
  error: any;
  itemListAddErrors: ICartItemListError[];
}

// Strong Typing end

// Initialize State start
export const initialState: ShopCartState = {
  carts: [],
  currentCartId: null,
  cartItems: null,
  error: null,
  cart: null,
  currentDefaultCartEtag: null,
  updateItemInCartData: {
    data: {
      type: 'items',
      attributes: {
        quantity: null,
      },
    },
  },
  isMiniCartOpen: false,
  showNewItemAddedNotification: false,
  pendingCartSwitchDecision: null,
  lastAddedItemName: '',
  isCartEmpty: true,
  addItemToCartOperationInProgress: false,
  loadingCartDataInProgress: false,
  itemsInQueueToCart: [],
  cartItemsWithDetails: [],
  rfqActive: false,
  cartRules: [],
  cartOperationInProgress: false,
  sapMessages: [],
  included: [],
  addingToCartViaCsvInProgress: false,
  itemListAddErrors: [],
  priceDisputingPerItem: [],
  materialMasterNumbersPerCart: [],
  areAllDataLoadedForCheckout: true,
};
// Initialize State end

// Selectors start
const getShopCartFeatureState: MemoizedSelector<object, ShopCartState> = createFeatureSelector<ShopCartState>(shopName);

export const selectPendingCartSwitchDecision: MemoizedSelector<object, ICartSwitchDecision | null> = createSelector(
  getShopCartFeatureState,
  state => state.pendingCartSwitchDecision,
);

export const selectMiniCartState: MemoizedSelector<object, {
  isCartEmpty: boolean;
  isMiniCartOpen: boolean;
  showNewItemAddedNotification: boolean;
  showCartSwitchDecisionModal: boolean;
}> = createSelector(
  getShopCartFeatureState,
  state => {
    return {
      isCartEmpty: state.isCartEmpty,
      isMiniCartOpen: state.isMiniCartOpen,
      showNewItemAddedNotification: state.showNewItemAddedNotification,
      showCartSwitchDecisionModal: !!state.pendingCartSwitchDecision,
    };
  },
);

export const getShopCartPageData: MemoizedSelector<object, {
  cartItems: any;
  loadingCartDataInProgress: boolean;
  cartId: string | null;
  addItemToCartOperationInProgress: boolean
}> = createSelector(
  getShopCartFeatureState,
  state => {
    return {
      cartItems: state.cartItems,
      loadingCartDataInProgress: state.loadingCartDataInProgress,
      cartId: state.currentCartId,
      addItemToCartOperationInProgress: state.addItemToCartOperationInProgress,
    };
  },
);

export const getCarts: MemoizedSelector<object, Array<ICart>> = createSelector(
  getShopCartFeatureState,
  state => state.carts,
);

export const selectCartsBaseInfo: MemoizedSelector<object, ICartBaseInformation[]> = createSelector(
  getShopCartFeatureState,
  state => {
    return state.carts.map((cart: ICart): ICartBaseInformation => ({
      id: cart.id,
      name: CartUtils.getCartName(cart),
      approverId: cart.attributes?.approverDetails?.approverId ?? '',
    }));
  },
);

export const selectDefaultCartNameAndETag: MemoizedSelector<object, {
  eTag: string | null;
  name: string
}> = createSelector(
  getShopCartFeatureState,
  state => ({
    eTag: state.currentDefaultCartEtag,
    name: state.carts.find(cart => cart.id === state.currentCartId)?.attributes.name ?? '',
  }),
);

export const selectCurrentCart: MemoizedSelector<object, any> = createSelector(
  getShopCartFeatureState,
  state => state.cart,
);

export const selectCurrentCartId: MemoizedSelector<object, string | null> = createSelector(
  getShopCartFeatureState,
  state => state.currentCartId,
);


export const selectCartItems: MemoizedSelector<object, any> = createSelector(
  getShopCartFeatureState,
  state => state.cartItems,
);

export const getCartsItems: MemoizedSelector<object, any[]> = createSelector(
  getShopCartFeatureState,
  state => {
    return state.cartItems?.included || state.included ? state.included : [];
  },
);

export const getAllCartsItems: MemoizedSelector<object, any[]> = createSelector(
  getShopCartFeatureState,
  state => {
    return state.included;
  },
);

export const getUpdateItemInCartData: MemoizedSelector<object, ShopCartState['updateItemInCartData']> = createSelector(
  getShopCartFeatureState,
  state => state.updateItemInCartData,
);

export const lastAddedItemName: MemoizedSelector<object, string> = createSelector(
  getShopCartFeatureState,
  state => state.lastAddedItemName,
);

export const isCartEmpty: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.isCartEmpty,
);

export const isAddItemToCartOperationInProgress: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.addItemToCartOperationInProgress,
);

export const itemsInQueueToCart: MemoizedSelector<object, Array<string>> = createSelector(
  getShopCartFeatureState,
  state => state.itemsInQueueToCart,
);

export const cartItemsWithDetails: MemoizedSelector<object, Array<any>> = createSelector(
  getShopCartFeatureState,
  state => state.cartItemsWithDetails,
);

export const selectCartItemsTotalWeight: MemoizedSelector<object, ITotalWeight> = createSelector(
  getShopCartFeatureState,
  state => CartUtils.calculateCartTotalWeight(state.cartItemsWithDetails),
);

export const selectCurrentCartCurrency: MemoizedSelector<object, string | undefined> = createSelector(
  getShopCartFeatureState,
  state => state.carts.find(cart => cart.id === state.currentCartId)?.attributes.currency,
);

export const selectRfqActive: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.rfqActive,
);

export const selectCartRules: MemoizedSelector<object, Array<ICartRule>> = createSelector(
  getShopCartFeatureState,
  state => state.cartRules,
);

export const selectCartOperationInProgress: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.cartOperationInProgress,
);

export const selectLoadingCartDataInProgress: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.loadingCartDataInProgress,
);

export const selectSapMessages: MemoizedSelector<object, Array<ISapMessage>> = createSelector(
  getShopCartFeatureState,
  state => state.sapMessages,
);

export const selectAddToCartViaCsvProgress: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.addingToCartViaCsvInProgress,
);

export const selectAddToCartViaCsvErrors: MemoizedSelector<object, ICartItemListError[]> = createSelector(
  getShopCartFeatureState,
  state => state.itemListAddErrors,
);

export const selectPrefilledBillToAddressId: MemoizedSelector<object, string | undefined> = createSelector(
  getShopCartFeatureState,
  state => state.carts.find(cart => cart.id === state.currentCartId)?.attributes.bpPrefilledBillToAddressId,
);

export const selectPrefilledShipToAddressId: MemoizedSelector<object, string | undefined> = createSelector(
  getShopCartFeatureState,
  state => state.carts.find(cart => cart.id === state.currentCartId)?.attributes.bpPrefilledShipToAddressId,
);

export const selectPriceDisputingPerItem: MemoizedSelector<object, IPriceDisputingPerItem[]> = createSelector(
  getShopCartFeatureState,
  state => state.priceDisputingPerItem,
);

export const selectIsPriceDisputingSetInCart: MemoizedSelector<object, boolean | undefined> = createSelector(
  getShopCartFeatureState,
  state => state.cart?.attributes?.isPriceDisputingSet,
);

export const selectMaterialMasterNumbersByItemId: (cartId: string, itemId: string) => MemoizedSelector<object, IMaterialMasterNumbersPerItem | undefined> = (cartId: string, itemId: string) => createSelector(
  getShopCartFeatureState,
  state => state.materialMasterNumbersPerCart.find(
    cart => cart.cartId === cartId,
  )?.materialMasterNumbersPerItem.find(item => item.itemId === itemId),
);

export const selectAllMaterialMasterNumbersInCart: (cartId: string) => MemoizedSelector<object, IMaterialMasterNumbersPerItem[] | undefined> = (cartId: string) => createSelector(
  getShopCartFeatureState,
  state =>
    state?.materialMasterNumbersPerCart?.find(
      cart => cart.cartId === cartId,
    )?.materialMasterNumbersPerItem,
);

export const areAllDataLoadedForCheckout: MemoizedSelector<object, boolean> = createSelector(
  getShopCartFeatureState,
  state => state.areAllDataLoadedForCheckout,
);
// Selectors end

// Reducer start
export const ShopCartReducer: ActionReducer<ShopCartState, Action<string>> = createReducer(
  initialState,
  on(ShopCartActions.clearCart, (): ShopCartState => {
    localStorage.removeItem(shopName);
    return {
      ...initialState,
    };
  }),
  on(ShopCartActions.loadCurrentCartAndCartItems, (state): ShopCartState => {
    return {
      ...state,
      loadingCartDataInProgress: true,
    };
  }),
  on(ShopCartActions.loadCurrentCart, (state): ShopCartState => {
    return {
      ...state,
      loadingCartDataInProgress: true,
    };
  }),
  on(ShopCartActions.loadCurrentCartItemsSuccess, (state): ShopCartState => {
    return {
      ...state,
      loadingCartDataInProgress: false,
    };
  }),
  on(ShopCartActions.loadCartItemsSuccess, (state, action): ShopCartState => {
    //merge locally stored price disputing data with BE price disputing data
    const mergedPriceDisputingData: IPriceDisputingPerItem[] = getMergedPriceDisputingData(state.priceDisputingPerItem, action.cartItems?.included?.filter(item => item.type === 'items') ?? []);
    return {
      ...state,
      cart: {
        ...action.cartItems.data,
        attributes: {
          ...action.cartItems.data.attributes,
          isPriceDisputingSet: isPriceDisputingSetInCart(mergedPriceDisputingData),
        },
      },
      cartItems: action.cartItems,
      isCartEmpty: !action.cartItems?.included,
      error: null,
      addItemToCartOperationInProgress: state.itemsInQueueToCart.length > 0,
      sapMessages: extractSapMessages(action.cartItems.included ?? []),
      priceDisputingPerItem: mergedPriceDisputingData,
      currentDefaultCartEtag: action.cartItems.eTag ?? '',
      materialMasterNumbersPerCart: processMaterialMasterNumbers(
        PayloadUtils.getItemsWithDetailsFromInclude(action.cartItems.included, true),
        action.cartItems.data.id,
      ),
    };
  }),
  on(ShopCartActions.loadCartItemsFail, (state, action): ShopCartState => {
    return {
      ...state,
      cartItems: null,
      error: 'State - Error: ' + action.error,
      addItemToCartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.loadCartsSuccess, (state, action): ShopCartState => {
    return {
      ...state,
      carts: action.carts.data,
      included: getUpdatedIncludesList(state.included, action.carts.included ?? []),
      error: null,
    };
  }),
  on(ShopCartActions.loadCartsFail, (state, action): ShopCartState => {
    return {
      ...state,
      carts: [],
      error: 'State - Error: ' + action.error,
      itemsInQueueToCart: [],
    };
  }),
  on(ShopCartActions.postCartSuccess, (state, action): ShopCartState => {
    return {
      ...state,
      currentCartId: action.newCartData.data.id,
      currentDefaultCartEtag: action.newCartData.eTag ?? '',
      included: getUpdatedIncludesList(state.included, action.newCartData.included ?? []),
      cart: action.newCartData.data,
      carts: addNewDefaultCart(state.carts, action.newCartData.data),
      sapMessages: extractSapMessages(action.newCartData.included ?? []),
      cartItemsWithDetails: [],
      cartItems: null,
      cartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.postCartFail, (state, action): ShopCartState => {
    return {
      ...state,
      currentCartId: null,
      error: 'State - Error: ' + action.error,
      itemsInQueueToCart: [],
    };
  }),
  on(ShopCartActions.removeItemFromQueue, (state, action): ShopCartState => {
    const newQueue: string[] = state.itemsInQueueToCart.filter(item => item !== action.sku);
    return {
      ...state,
      error: action.error,
      itemsInQueueToCart: newQueue,
    };
  }),
  on(ShopCartActions.loadCartSuccess, (state, action): ShopCartState => {
    return {
      ...state,
      cart: action.cart,
      currentCartId: action.cart.id,
      error: null,
      cartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.loadCartFail, (state, action): ShopCartState => {
    return {
      ...state,
      cart: null,
      error: 'State - Error: ' + action.error,
    };
  }),
  on(ShopCartActions.deleteCart, (state: ShopCartState): ShopCartState => {
    return {
      ...state,
      showNewItemAddedNotification: false,
      cartOperationInProgress: true,
      priceDisputingPerItem: [],
    };
  }),
  on(ShopCartActions.deleteCartSuccess, (state, action): ShopCartState => {
    const cartIncludes: Set<string> = new Set([EGlueResource.SAP_MESSAGES, EGlueResource.SAP_ITEM_AVAILABILITIES]);
    const isDeletedCartInclude: (include: IBaseModel) => boolean = function (include: IBaseModel) {
      return cartIncludes.has(include.type) && include.id === action.cartId;
    };
    return {
      ...state,
      carts: state.carts.filter((cart) => cart.id !== action.cartId),
      included: state.included.filter((include: IBaseModel) => !isDeletedCartInclude(include)),
      currentCartId: action.isDefault ? null : state.currentCartId,
      sapMessages: action.isDefault ? [] : state.sapMessages,
      error: null,
      addItemToCartOperationInProgress: false,
      cartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.deleteCartFail, (state, action): ShopCartState => {
    return {
      ...state,
      addItemToCartOperationInProgress: false,
      cartOperationInProgress: false,
      error: 'State - Error: ' + action.error,
    };
  }),
  on(ShopCartActions.addItemToCartSuccess, (state, action): ShopCartState => {
    const newQueue: string[] = state.itemsInQueueToCart.filter(item => item !== action.product.sku);
    return {
      ...state,
      error: null,
      addItemToCartOperationInProgress: false,
      included: getUpdatedIncludesList(state.included, action.cartResponse.included ?? []),
      currentDefaultCartEtag: action.cartResponse.eTag ?? '',
      itemsInQueueToCart: newQueue,
      lastAddedItemName: action.product.name,
      isCartEmpty: false,
      carts: getUpdatedCartsList(state.carts, action.cartResponse.data),
      sapMessages: extractSapMessages(action.cartResponse.included ?? []),
    };
  }),
  on(ShopCartActions.addItemToCartError, (state, action): ShopCartState => {
    return {
      ...state,
      error: action.error,
      addItemToCartOperationInProgress: false,
      itemsInQueueToCart: [],
    };
  }),
  on(ShopCartActions.deleteItemFromCart, (state, {itemId}): ShopCartState => {
    //remove price disputing data for deleted item
    const priceDisputingDataAfterItemDeletion: IPriceDisputingPerItem[] = removePriceDisputingForDeletedItem(state.priceDisputingPerItem, itemId);

    return {
      ...state,
      addItemToCartOperationInProgress: true,
      cartOperationInProgress: true,
      priceDisputingPerItem: priceDisputingDataAfterItemDeletion,
      cart: {
        ...state.cart,
        attributes: {
          ...state.cart.attributes,
          isPriceDisputingSet: isPriceDisputingSetInCart(priceDisputingDataAfterItemDeletion),
        },
      },
    };
  }),
  on(ShopCartActions.deleteItemFromCartSuccess, (state): ShopCartState => {
    return {
      ...state,
      cartOperationInProgress: true,
      error: null,
    };
  }),
  on(ShopCartActions.deleteItemFromCartFail, (state, action): ShopCartState => {
    return {
      ...state,
      cartOperationInProgress: false,
      error: 'State - Error: ' + action.error,
    };
  }),
  on(ShopCartActions.deleteItemsFromCart, (state): ShopCartState => {
    return {
      ...state,
      addItemToCartOperationInProgress: true,
      cartOperationInProgress: true,
      loadingCartDataInProgress: true,
    };
  }),
  on(ShopCartActions.deleteItemsFromCartSuccess, (state, action): ShopCartState => {
    const cartIncludes: Set<string> = new Set([EGlueResource.SAP_MESSAGES, EGlueResource.SAP_ITEM_AVAILABILITIES]);
    const isDeletedCartInclude: (include: IBaseModel) => boolean = function (include: IBaseModel) {
      return cartIncludes.has(include.type) && include.id === action.cartResponse.data.id;
    };
    return {
      ...state,
      cartOperationInProgress: false,
      addItemToCartOperationInProgress: false,
      loadingCartDataInProgress: false,
      error: null,
      currentDefaultCartEtag: action.cartResponse.eTag ?? '',
      itemsInQueueToCart: [],
      lastAddedItemName: '',
      isCartEmpty: true,
      priceDisputingPerItem: [],
      carts: getUpdatedCartsList(state.carts, action.cartResponse.data),
      cart: {
        ...state.cart,
        attributes: {
          ...state.cart.attributes,
          isPriceDisputingSet: false,
        },
      },
      cartItems: [],
      cartItemsWithDetails: [],
      sapMessages: [],
      included: state.included.filter((include: IBaseModel) => !isDeletedCartInclude(include)),
    };
  }),
  on(ShopCartActions.deleteItemsFromCartError, (state, action): ShopCartState => {
    return {
      ...state,
      cartOperationInProgress: false,
      addItemToCartOperationInProgress: false,
      loadingCartDataInProgress: false,
      error: 'State - Error: ' + action.error,
    };
  }),
  on(ShopCartActions.setUpdateItemInCartData, (state, action): ShopCartState => {
    return {
      ...state,
      updateItemInCartData: {
        data: {
          type: 'items',
          attributes: {
            quantity: action.quantity,
          },
        },
      },
      error: null,
      addItemToCartOperationInProgress: true,
      cartOperationInProgress: true,
    };
  }),
  on(ShopCartActions.updateItemInCartLoadCartItems, (state): ShopCartState => {
    return {
      ...state,
      addItemToCartOperationInProgress: true,
      cartOperationInProgress: true,
    };
  }),
  on(ShopCartActions.updateItemInCartLoadCartItemsSuccess, (state, action): ShopCartState => {
    return {
      ...state,
      cart: action.updatedItemInCartAndLoadedCartItems.data,
      cartItems: action.updatedItemInCartAndLoadedCartItems,
      error: null,
      addItemToCartOperationInProgress: false,
      cartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.updateItemInCartLoadCartItemsFail, (state, action): ShopCartState => {
    return {
      ...state,
      error: 'State - Error: ' + action.error,
      addItemToCartOperationInProgress: false,
      cartOperationInProgress: false,
      itemsInQueueToCart: [],
    };
  }),
  on(ShopCartActions.updateShopCartAfterItemUpdate, (state, action): ShopCartState => {
    const included: any[] = action.payload.included ?? [];
    const mergedPriceDisputingData: IPriceDisputingPerItem[] = getMergedPriceDisputingData(state.priceDisputingPerItem, included.filter(item => item.type === 'items'));
    const cartRules: any[] = included.filter(include => include.type === EGlueResource.CART_RULES);
    const cartDetails: IBaseModel[] = PayloadUtils.getItemsWithDetailsFromInclude(included, true);
    const rfqProducts: IBaseModel[] = included.filter(include => include.type === EGlueResource.RFQ_PRODUCTS);

    return {
      ...state,
      cartItemsWithDetails: cartDetails,
      error: null,
      cartRules: cartRules || [],
      rfqActive: rfqProducts !== undefined && rfqProducts?.length > 0,
      cart: {
        ...action.payload.data,
        attributes: {
          ...action.payload.data.attributes,
          isPriceDisputingSet: isPriceDisputingSetInCart(mergedPriceDisputingData),
        },
      },
      priceDisputingPerItem: mergedPriceDisputingData,
      cartItems: action.payload,
      sapMessages: extractSapMessages(included),
      included: getUpdatedIncludesList(state.included, included),
      carts: getUpdatedCartsList(state.carts, action.payload.data),
      loadingCartDataInProgress: false,
      currentDefaultCartEtag: action.payload.eTag ?? '',
      addItemToCartOperationInProgress: false,
      cartOperationInProgress: false,
      materialMasterNumbersPerCart: filterProductsWithMaterialMasterNumbers(
        state.materialMasterNumbersPerCart,
        action.payload.data.id,
        getIdsFromIncluded(
          included,
          'items',
          'id',
        ),
      ),
    };
  }),
  on(ShopCartActions.updateCartSystemDetailsSuccess, (state, action) => {
    const mergedPriceDisputingData: IPriceDisputingPerItem[] = getMergedPriceDisputingData(
      state.priceDisputingPerItem,
      action.cart?.included?.filter(item => item.type === EGlueResource.CART_ITEMS) ?? [],
    );
    return {
      ...state,
      cart: action.cart.data,
      cartItems: action.cart,
      priceDisputingPerItem: mergedPriceDisputingData,
      carts: getUpdatedCartsList(state.carts, action.cart.data),
      loadingCartDataInProgress: false,
      cartOperationInProgress: false,
      error: null,
      currentDefaultCartEtag: action.cart.eTag ?? null,
      sapMessages: extractSapMessages(action.cart.included ?? []),
    };
  }),
  on(ShopCartActions.toggleMiniCart, (state, action) => {
    return {
      ...state,
      isMiniCartOpen: action.isOpen,
      showNewItemAddedNotification: false,
    };
  }),
  on(ShopCartActions.openMiniCartAndShowNotificationAfterAddToCart, (state) => {
    return {
      ...state,
      isMiniCartOpen: true,
      showNewItemAddedNotification: true,
    };
  }),
  on(ShopCartActions.requestCartSwitchDecision, (state, action) => ({
    ...state,
    pendingCartSwitchDecision: action.pending,
    addItemToCartOperationInProgress: false,
    itemsInQueueToCart: state.itemsInQueueToCart.filter(sku => sku !== action.pending.productSku),
  })),
  on(ShopCartActions.switchToNewCart, (state) => ({...state, pendingCartSwitchDecision: null})),
  on(ShopCartActions.stayOnCurrentCart, (state, action): ShopCartState => {
    const {data: newCart, included} = action.pending.cartResponse;
    return {
      ...state,
      pendingCartSwitchDecision: null,
      carts: [...(state.carts ?? []), {...newCart, attributes: {...newCart.attributes, isDefault: false}}],
      included: getUpdatedIncludesList(state.included ?? [], included ?? []),
    };
  }),
  on(ShopCartActions.stayOnCurrentCartSuccess, (state, action): ShopCartState => ({
    ...state,
    currentDefaultCartEtag: action.currentCart.eTag ?? null,
  })),
  on(ShopCartActions.addLastAddedItemName, (state, action) => {
    return {
      ...state,
      lastAddedItemName: action.itemName,
    };
  }),
  on(ShopCartActions.setCartStatus, (state, action) => {
    return {
      ...state,
      isCartEmpty: action.status,
    };
  }),
  on(ShopCartActions.postNewCartWithItem, ShopCartActions.postNewCartWithItemForSwitchDecision, (state) => {
    return {
      ...state,
      addItemToCartOperationInProgress: true,
    };
  }),
  on(ShopCartActions.addItemToCart, (state) => {
    return {
      ...state,
      addItemToCartOperationInProgress: true,
    };
  }),
  on(ShopCartActions.addItemToCartInProgress, (state, action) => {
    return {
      ...state,
      addItemToCartOperationInProgress: action?.status,
    };
  }),
  on(ShopCartActions.addItemFailAfterCartLoad, (state, action) => {
    return {
      ...state,
      error: action.error,
      itemsInQueueToCart: [],
    };
  }),
  on(ShopCartActions.addItemToQueue, (state, action) => {
    if (state.itemsInQueueToCart.includes(action.sku)) {
      return {
        ...state,
      };
    }
    return {
      ...state,
      addItemToCartOperationInProgress: true,
      itemsInQueueToCart: [...state.itemsInQueueToCart, action.sku],
    };
  }),
  on(ShopCartActions.setDefaultCartItems, (state, action) => {
    let cartDetails: IBaseModel[] = [];
    if (!action.cartItems?.included) {
      const emptyCart: boolean = action.cartItems.data.attributes.totalItemsQuantity === 0;
      return {
        ...state,
        isCartEmpty: emptyCart,
        cartItemsWithDetails: emptyCart ? [] : state.cartItemsWithDetails,
        rfqActive: false,
      };
    }
    cartDetails = PayloadUtils.getItemsWithDetailsFromInclude(action.cartItems.included, true);
    const rfqProducts: IBaseModel[] = action.cartItems.included.filter((include: IBaseModel) => include.type === EGlueResource.RFQ_PRODUCTS);
    const cartRules: ICartRule[] = action.cartItems.included.filter((include: ICartRule) => include.type === EGlueResource.CART_RULES);
    return {
      ...state,
      cartItemsWithDetails: cartDetails,
      rfqActive: rfqProducts !== undefined && rfqProducts?.length > 0,
      cartRules: cartRules || [],
    };
  }),
  on(ShopCartActions.switchDefaultCart, (state): ShopCartState => {
    return {
      ...state,
      cartOperationInProgress: true,
    };
  }),
  on(ShopCartActions.switchDefaultCartSuccess, (state, action): ShopCartState => {
    const oldDefaultCartId: string | null = state.currentCartId;
    let carts: ICart[] = getUpdatedCartsList(state.carts, action.cart.data);
    if (carts.length >= 1) {
      carts = carts.map(
        (cart) => cart.id === oldDefaultCartId ? {...cart, attributes: {...cart.attributes, isDefault: false}} : cart,
      );

      return {
        ...state,
        carts,
        sapMessages: extractSapMessages(action.cart.included ?? []),
        currentCartId: action.cart.data.id,
        currentDefaultCartEtag: action.cart.eTag ?? null,
        cartOperationInProgress: false,
      };
    }

    return {
      ...state,
      cartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.switchDefaultCartFail, (state, action): ShopCartState => {
    return {
      ...state,
      error: 'State - Error: ' + action.error,
      cartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.setCartOperationStateAfterDelete, (state): ShopCartState => ({
    ...state,
    cartOperationInProgress: false,
    loadingCartDataInProgress: false,
  })),
  on(ShopCartActions.handleAddToCartResetState, (state, action): ShopCartState => {
    return {
      ...state,
      error: 'State - Error: ' + action.error.detail,
      itemsInQueueToCart: [],
      addItemToCartOperationInProgress: false,
    };
  }),
  on(ShopCartActions.startCartOperation, (state) => {
    return {
      ...state,
      cartOperationInProgress: true,
      loadingCartDataInProgress: true,
    };
  }),
  on(ShopCartActions.stopCartOperation, (state) => {
    return {
      ...state,
      cartOperationInProgress: false,
      loadingCartDataInProgress: false,
    };
  }),
  on(ShopCartActions.deleteCpqCart, ShopCartActions.quoteSummaryPageDeleteQuote, (state) => {
    return {
      ...state,
      cartOperationInProgress: true,
    };
  }),
  on(ShopCartActions.deleteCpqCartFail, (state, action) => {
    return {
      ...state,
      cartOperationInProgress: false,
      error: action.error,
    };
  }),
  on(ShopCartActions.addToCartFromCsvFile, (state) => {
    return {
      ...state,
      addingToCartViaCsvInProgress: true,
      itemListAddErrors: [],
    };
  }),
  on(ShopCartActions.addToCartFromCsvFileSuccess, (state, action) => {
    return {
      ...state,
      itemListAddErrors: action.cart.data.attributes.itemListAddErrors ?? [],
      addingToCartViaCsvInProgress: false,
    };
  }),
  on(ShopCartActions.addToCartFromCsvFileFail, (state) => {
    return {
      ...state,
      addingToCartViaCsvInProgress: false,
    };
  }),
  on(ShopCartActions.setBillToAddressIdAction, (state: ShopCartState, {billToAddressSapId}) => {
    const updatedCarts: ICart[] = state.carts.map(cart => {
      if (cart.id === state.currentCartId) {
        cart = {
          ...cart,
          attributes: {
            ...cart.attributes,
            bpPrefilledBillToAddressId: billToAddressSapId,
          },
        };
      }
      return cart;
    });
    return {
      ...state,
      carts: updatedCarts,
    };
  }),
  on(ShopCartActions.clearBillToAddressIdAction, (state) => {
    const updatedCarts: ICart[] = state.carts.map(cart => {
      if (cart.id === state.currentCartId) {
        cart = {
          ...cart,
          attributes: {
            ...cart.attributes,
            bpPrefilledBillToAddressId: undefined,
          },
        };
      }
      return cart;
    });
    return {
      ...state,
      carts: updatedCarts,
    };
  }),
  on(ShopCartActions.setShipToAddressIdAction, (state: ShopCartState, {shipToAddressSapId}) => {
    const updatedCarts: ICart[] = state.carts.map(cart => {
      if (cart.id === state.currentCartId) {
        cart = {
          ...cart,
          attributes: {
            ...cart.attributes,
            bpPrefilledShipToAddressId: shipToAddressSapId,
          },
        };
      }
      return cart;
    });
    return {
      ...state,
      carts: updatedCarts,
    };
  }),
  on(ShopCartActions.clearShipToAddressIdAction, (state) => {
    const updatedCarts: ICart[] = state.carts.map(cart => {
      if (cart.id === state.currentCartId) {
        cart = {
          ...cart,
          attributes: {
            ...cart.attributes,
            bpPrefilledShipToAddressId: undefined,
          },
        };
      }
      return cart;
    });
    return {
      ...state,
      carts: updatedCarts,
    };
  }),
  on(ShopCartActions.addOrUpdatePriceDisputingPerItemInCartAction, (state, {updatedPriceDisputing}) => {
    if (state?.priceDisputingPerItem) {
      const index: number = state?.priceDisputingPerItem?.findIndex(priceDisputing => {
        return priceDisputing?.itemId === updatedPriceDisputing.itemId;
      });

      if (index === -1) {
        state.priceDisputingPerItem.push(updatedPriceDisputing);
      } else {
        state.priceDisputingPerItem[index] = updatedPriceDisputing;
      }
    } else {
      state.priceDisputingPerItem = [];
      state.priceDisputingPerItem.push(updatedPriceDisputing);
    }

    return {
      ...state,
      priceDisputingPerItem: state.priceDisputingPerItem,
      cart: {
        ...state.cart,
        attributes: {
          ...state.cart.attributes,
          isPriceDisputingSet: isPriceDisputingSetInCart(state.priceDisputingPerItem),
        },
      },
    };
  }),
  on(ShopCartActions.clearPriceDisputing, (state) => {
    return {
      ...state,
      priceDisputingPerItem: [],
    };
  }),
  on(ShopCartActions.clearMaterialMasterNumbersForCart, (state, {cartId}) => {
    return {
      ...state,
      materialMasterNumbersPerCart: state.materialMasterNumbersPerCart.filter(cart => cart.cartId !== cartId),
    };
  }),
  on(ShopCartActions.addOrUpdateMaterialMasterNumbersPerItemAction, (state, {materialMasterNumbers, cartId}) => {
    return {
      ...state,
      materialMasterNumbersPerCart: [...addOrUpdateItemInArray(state?.materialMasterNumbersPerCart, materialMasterNumbers, cartId)],
    };
  }),
  on(ShopCartActions.areAllDataLoadedForCheckout, (state, {areAllDataLoaded}) => {
      return {
        ...state,
        areAllDataLoadedForCheckout: areAllDataLoaded,
      };
    },
  ),
);

// Reducer end

/**
 * Remove price disputing data for previously deleted item.
 *
 *
 * @param {IPriceDisputingPerItem[]} priceDisputingPerItem
 * @param {string} itemId
 * @return {IPriceDisputingPerItem[]}
 */
function removePriceDisputingForDeletedItem(priceDisputingPerItem: IPriceDisputingPerItem[], itemId: string): IPriceDisputingPerItem[] {
  return priceDisputingPerItem.filter(itemPriceDisputing => itemPriceDisputing?.itemId !== itemId);
}

/**
 * Merge FE and BE price disputing data.
 *
 *
 * @param {IPriceDisputingPerItem[]} priceDisputingPerItem
 * @param {ICartItem[]} items
 * @return {IPriceDisputingPerItem[]}
 */
function getMergedPriceDisputingData(priceDisputingPerItem: IPriceDisputingPerItem[], items: ICartItem[]): IPriceDisputingPerItem[] {
  if (priceDisputingPerItem?.length > 0) {
    return priceDisputingPerItem;
  }

  return items.reduce((acc: IPriceDisputingPerItem[], item) => {
    if (item?.attributes?.priceDisputing?.isSet && item.id) {
      acc.push({
        itemId: item.id,
        priceDisputing: {
          isSet: item.attributes.priceDisputing?.isSet ?? false,
          message: item.attributes.priceDisputing?.message ?? '',
        },
      });
    }
    return acc;
  }, []);
}

/**
 * Determine if price disputing is set on the cart level.
 *
 *
 * @param {IPriceDisputingPerItem[]} items
 * @return {boolean}
 */
function isPriceDisputingSetInCart(items: IPriceDisputingPerItem[]): boolean {
  if (!items) {
    return false;
  }

  return items.some(item => item?.priceDisputing?.isSet);
}

/**
 * Update existing cart in the list with new data.
 *
 * @param {Array<ICart>} carts
 * @param {ICart} updatedCartData
 *
 * @returns {Array<ICart>}
 */
function getUpdatedCartsList(
  carts: Array<ICart>,
  updatedCartData: ICart,
): Array<ICart> {
  return carts.map(cart => cart.id === updatedCartData.id ? updatedCartData : cart);
}

/**
 * Add a new default cart to the list, marking all existing carts as non-default.
 *
 * @param {Array<ICart>} carts
 * @param {ICart} newCartData
 *
 * @returns {Array<ICart>}
 */
function addNewDefaultCart(
  carts: Array<ICart>,
  newCartData: ICart,
): Array<ICart> {
  return [
    ...carts.map(cart => ({...cart, attributes: {...cart.attributes, isDefault: false}})),
    newCartData,
  ];
}

/**
 * Return previous list of included resources with new includes from incoming
 * response. "New" in this case means includes objects having a combination
 * of type and id values that have not been returned previously;.
 *
 * @param {Array<IBaseModel>} included
 * @param {Array<IBaseModel>} updatedIncludes
 * @returns {Array<IBaseModel>}
 */
function getUpdatedIncludesList(
  included: Array<IBaseModel>,
  updatedIncludes: Array<IBaseModel>,
): Array<IBaseModel> {
  const newIncludes: IBaseModel[] = updatedIncludes.filter((includedResource) =>
    !included.some(
      (resource) => resource.type === includedResource.type && resource.id === includedResource.id,
    ),
  );
  return [...included, ...newIncludes];
}

/**
 * Extract SAP messages from included cart resources.
 *
 * @param {Array<IBaseModel>} cartIncludes
 * @returns {Array<ISapMessage>}
 */
function extractSapMessages(
  cartIncludes: Array<IBaseModel>,
): Array<ISapMessage> {
  const messageInclude: IBaseModel | undefined = cartIncludes.find(item => item.type === EGlueResource.SAP_MESSAGES);
  return (messageInclude as any)?.attributes?.messages || []; //TODO: messageInclude has set type that do not contain `attributes` field, need to be fixed in the future - for now, ts-ignore is replaced with 'as any'
}

/**
 * Check whether to add a new item or update existing one
 * @param {IMaterialMasterNumbersPerCart[]} array
 * @param item
 * @param cartId
 * @returns {IMaterialMasterNumbersPerItem[]}
 */
function addOrUpdateItemInArray(array: IMaterialMasterNumbersPerCart[], item: IMaterialMasterNumbersPerItem, cartId: string): any[] {
  const indexOfCart: number = array.findIndex(existing => existing.cartId === cartId);
  const updatedArray: IMaterialMasterNumbersPerCart[] = [...array];

  if (indexOfCart === -1) {
    updatedArray.push({
      cartId: cartId,
      materialMasterNumbersPerItem: [item],
    });
  } else {
    const materialMasterNumbersPerItem: IMaterialMasterNumbersPerItem[] = array[indexOfCart]?.materialMasterNumbersPerItem;
    const indexOfItem: number = materialMasterNumbersPerItem
      .findIndex(existing => existing.itemId === item.itemId);
    if (indexOfItem === -1) {
      materialMasterNumbersPerItem.push(item);
    } else {
      materialMasterNumbersPerItem?.splice(indexOfItem, 1, item);
    }
    const cartMaterialNumbers: IMaterialMasterNumbersPerCart = {
      cartId: cartId,
      materialMasterNumbersPerItem: materialMasterNumbersPerItem,
    };
    updatedArray.splice(indexOfCart, 1, cartMaterialNumbers);
  }

  return [...updatedArray];
}

/**
 * Process non-empty material master numbers for a given cart items.
 * @param {any[]} items
 * @param {string} cartId
 * @returns {IMaterialMasterNumbersPerCart[]}
 * */
export function processMaterialMasterNumbers(
  items: any[],
  cartId: string,
): IMaterialMasterNumbersPerCart[] {
  const materialMasterNumbersPerItem: IMaterialMasterNumbersPerItem[] = items
    .filter(item => item.attributes?.materialMasterNumbers)
    .map(item => ({
      itemId: item.id,
      ...Object.fromEntries(
        Object.entries(item.attributes!.materialMasterNumbers).filter(([, value]) => value),
      ),
    }));

  return materialMasterNumbersPerItem.length
    ? [{cartId, materialMasterNumbersPerItem}]
    : [];
}

/**
 * Get any value from included array of any type
 * @param {any[]} includedItems
 * @param {string} type
 * @param {string} pushValue
 * @returns {string[]}
 */
function getIdsFromIncluded(includedItems: any[], type: string, pushValue: string): string[] {
  return includedItems.reduce(function (filtered: string[], items: any) {
    if (items.type === type) {
      filtered.push(items[pushValue]);
    }
    return filtered;
  }, []);
}

/**
 * Filter out all items, which ids are not in `ids` parameter
 * @param {IMaterialMasterNumbersPerCart[]} materialMasterNumbers
 * @param cartId
 * @param {string[]} ids
 * @returns {IMaterialMasterNumbersPerItem[]}
 */
function filterProductsWithMaterialMasterNumbers(materialMasterNumbers: IMaterialMasterNumbersPerCart[], cartId: string, ids: string[]): IMaterialMasterNumbersPerCart[] {
  const indexOfCart: number = materialMasterNumbers.findIndex(existing => existing.cartId === cartId);
  const masterNumbersInCart: IMaterialMasterNumbersPerItem[] =
          materialMasterNumbers?.[indexOfCart]?.materialMasterNumbersPerItem
            ?.filter((number) => number.itemId !== null && ids.includes(number.itemId));

  return materialMasterNumbers.splice(indexOfCart, 1, {
    cartId: cartId,
    materialMasterNumbersPerItem: masterNumbersInCart,
  });
}
